import React from 'react';
import PropTypes from 'prop-types';
import { AlertCircle } from 'lucide-react';

export const SMB_EXPENSE_CATEGORIES = [
  { key: 'cogs', label: 'Cost of Goods Sold / Direct Costs' },
  { key: 'operating_expenses', label: 'Operating Expenses' },
  { key: 'owner_compensation', label: 'Owner Compensation' },
  { key: 'one_time_expenses', label: 'One-Time / Non-Recurring Expenses' },
];

function ExpenseMappingRow({ account, categories, mapping, onChangeMapping }) {
  return (
    <div className="flex items-center gap-4 py-3 border-b border-border last:border-0">
      <div className="flex-1 min-w-0">
        <p className="text-[14px] text-foreground truncate">{account.name}</p>
        <p className="text-[12px] text-muted-foreground">{account.classification || 'Expense'}</p>
      </div>
      <div className="w-64 flex-shrink-0">
        <select
          value={mapping || ''}
          onChange={(e) => onChangeMapping(account.id, e.target.value || null)}
          className="w-full bg-background border border-input rounded-[8px] px-3 py-2 text-[13px] text-foreground focus:border-[hsl(var(--succeedo-orange))] focus:outline-none"
        >
          <option value="">— Not mapped —</option>
          {categories.map((cat) => (
            <option key={cat.key} value={cat.key}>{cat.label}</option>
          ))}
        </select>
      </div>
    </div>
  );
}

ExpenseMappingRow.propTypes = {
  account: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
    classification: PropTypes.string,
  }).isRequired,
  categories: PropTypes.arrayOf(PropTypes.shape({ key: PropTypes.string, label: PropTypes.string })).isRequired,
  mapping: PropTypes.string,
  onChangeMapping: PropTypes.func.isRequired,
};

ExpenseMappingRow.defaultProps = { mapping: null };

// ExpenseMappingTable — category legend + rows + coverage warning + Save
// CTA. Stateless table; the parent owns the accounts, mappings dict, and
// save handler.
export default function ExpenseMappingTable({
  accounts,
  mappings,
  loadingAccounts,
  savingMappings,
  saveSuccess,
  onChangeMapping,
  onSave,
}) {
  const mappedCount = Object.keys(mappings).length;

  return (
    <div className="bg-white rounded-[16px] p-6 shadow-sm border border-border">
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-1">
          <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: 'hsl(var(--succeedo-orange))' }} />
          <span className="font-mono text-[11px] uppercase text-muted-foreground tracking-wide">Expense Mapping</span>
        </div>
        <h2 className="text-[20px] font-semibold text-foreground">Map your expense accounts</h2>
        <p className="text-[14px] text-muted-foreground mt-1">
          Assign each QuickBooks expense account to a Valuation Studio category.
          Mapped accounts will be totalled and used to pre-fill the wizard.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-2 mb-6">
        {SMB_EXPENSE_CATEGORIES.map((cat) => (
          <div key={cat.key} className="bg-background border border-border rounded-[8px] px-3 py-2">
            <p className="text-[12px] font-medium text-foreground">{cat.label}</p>
            <p className="text-[11px] text-muted-foreground font-mono uppercase">{cat.key}</p>
          </div>
        ))}
      </div>

      {/* eslint-disable-next-line no-nested-ternary */}
      {loadingAccounts ? (
        <div className="py-8 text-center text-[14px] text-muted-foreground">
          Loading your QuickBooks expense accounts&hellip;
        </div>
      ) : accounts.length === 0 ? (
        <div className="py-8 text-center text-[14px] text-muted-foreground">
          No expense accounts found. Make sure your QuickBooks sync has completed.
        </div>
      ) : (
        <>
          {/* Coverage warning — unmapped accounts are dropped from the
              pre-filled total, so low coverage produces an artificially
              high reported EBITDA in the wizard. */}
          {mappedCount / accounts.length < 0.8 && (
            <div
              className="mb-4 flex items-start gap-3 rounded-[10px] p-3 border"
              style={{ backgroundColor: 'hsl(var(--succeedo-orange) / 0.08)', borderColor: 'hsl(var(--succeedo-orange) / 0.25)' }}
            >
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: 'hsl(var(--succeedo-orange))' }} />
              <p className="text-[13px] text-foreground/80">
                Only {mappedCount} of {accounts.length} expense accounts are mapped. Unmapped accounts are excluded from the
                Valuation Studio pre-fill, which can inflate your reported EBITDA. Map the remaining accounts for an accurate total.
              </p>
            </div>
          )}

          <div className="mb-4">
            {accounts.map((account) => (
              <ExpenseMappingRow
                key={account.id}
                account={account}
                categories={SMB_EXPENSE_CATEGORIES}
                mapping={mappings[account.id] || null}
                onChangeMapping={onChangeMapping}
              />
            ))}
          </div>

          <div className="flex items-center justify-between pt-4 border-t border-border">
            <p className="text-[13px] text-muted-foreground">
              {mappedCount} of {accounts.length} account{accounts.length !== 1 ? 's' : ''} mapped
            </p>
            <button
              type="button"
              onClick={onSave}
              disabled={savingMappings}
              className="px-6 py-2.5 rounded-full font-medium text-[14px] text-background transition-colors disabled:opacity-50"
              style={{ backgroundColor: 'hsl(var(--succeedo-orange))' }}
            >
              {/* eslint-disable-next-line no-nested-ternary */}
              {savingMappings ? 'Saving…' : (saveSuccess ? 'Saved ✓' : 'Save Mappings')}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

ExpenseMappingTable.propTypes = {
  accounts: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
  })).isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  mappings: PropTypes.object.isRequired,
  loadingAccounts: PropTypes.bool,
  savingMappings: PropTypes.bool,
  saveSuccess: PropTypes.bool,
  onChangeMapping: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

ExpenseMappingTable.defaultProps = {
  loadingAccounts: false,
  savingMappings: false,
  saveSuccess: false,
};
